import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useTranslate } from '@growtherapy/i18n';

import { IntentionNotSetBasicModal, IntentionNotSetModal } from '@grow-therapy-team/seedling-components';

import { useCreateSessionIntentionMutation } from '../../hooks/intentions/useCreateSessionIntentionMutation';
import { useMomentumPilotPatientAccessQuery } from '../../hooks/intentions/useMomentumPilotPatientAccessQuery';
import { useAppDependenciesContext } from '../../state';
import { useSendSessionIntentionSetCallback } from '../messages/useSendSessionIntentionSetCallback';
import { PROVIDER_SET_INTENTIONS_KEY } from './constants';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

export const IntentionNotSetModalWrapper = ({
  isOpen,
  onClose,
  clientName,
  providerImage,
  onSendPrompt,
  patientShortId,
  appVersion,
  clientUserShortId,
  appointmentShortId,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  providerImage: string;
  onSendPrompt: () => void;
  patientShortId: string;
  appVersion: string;
  clientUserShortId: string;
  appointmentShortId: string;
}) => {
  const { pilotAccess, loading: pilotAccessLoading } = useMomentumPilotPatientAccessQuery(
    patientShortId,
    PROVIDER_SET_INTENTIONS_KEY,
    appVersion,
  );
  const [createSessionIntention] = useCreateSessionIntentionMutation();
  const sendSessionIntentionSet = useSendSessionIntentionSetCallback();

  const { logger, toast } = useAppDependenciesContext();
  const t = useTranslate();

  if (pilotAccessLoading) {
    return <></>;
  }

  const handleSetIntention = async (intention: string) => {
    try {
      const result = await createSessionIntention({
        variables: {
          clientUserShortId,
          appointmentShortId,
          contentText: intention,
        },
      });

      const shortId = result.data?.createSessionIntention?.sessionIntention?.shortId;
      if (shortId) {
        await sendSessionIntentionSet(shortId);
        onClose();
      }
    } catch (error) {
      logger.error('Error setting session intention', { error });
      toast.error(t('telehealth-core:sessionIntention.couldNotSetIntentionForClient'));
    }
  };

  return (
    <ErrorBoundary>
      {pilotAccess?.enabled ? (
        <IntentionNotSetModal
          name={clientName}
          isOpen={isOpen}
          onClose={onClose}
          onSendPrompt={onSendPrompt}
          onSetIntention={handleSetIntention}
        />
      ) : (
        <IntentionNotSetBasicModal
          name={clientName}
          isOpen={isOpen}
          onClose={onClose}
          providerPhotoUrl={providerImage}
          onSendPrompt={onSendPrompt}
        />
      )}
    </ErrorBoundary>
  );
};
